// `messaging.receipt_sms.stopped.v1` — emitted by the messaging backend when a
// donor replies STOP to a receipt text (CON-555; POS SPEC F3.H H2/H9, D-040
// item 6 ask 4, D-058 item 3 STOP scope (a)).
//
// ONE EVENT PER ORGANIZATION. A STOP names only the donor's number; messaging
// resolves which nonprofits TEXTED that number from its own `sms_` outbox rows
// (status SENT or DELIVERED) and publishes one event for each, so the envelope's
// single `organizationId` stays the per-tenant concurrency key (Commitment 11).
// A nonprofit that holds the number but never texted it gets no event.
//
// NO PHONE NUMBER. The payload carries `toPhoneHash`, the lowercase hex sha256
// of the E.164 number (the same hash messaging stores on the row). The consumer
// suppresses only contacts whose CURRENT phone hashes to it, so a STOP from a
// number that has since moved to another contact never silences that contact.
//
// `originIdempotencyKey` is `<zernioEventId>:<organizationId>:<batchContentsHash>`.
// An organization that texted more than 100 contacts at the number gets one
// event per batch of at most 100; `batchContentsHash` is the sha256 of the
// batch's sorted contactIds, so a key always names the same contents. Zernio
// delivers at least once over about 51 hours, and messaging may re-publish
// after a crash before it records the event, so consumers MUST dedupe on it.
//
// Producer: backends/messaging (`Source: 'charitystack.messaging'`), the
//   `POST /api/messaging/webhooks/zernio` route.
// Consumer: backends/marketing — receipt-sms-stopped bridge → the
//   `receipt-sms-stopped` Inngest workflow, which applies the SMS-only INV-17
//   write (smsConsent UNSUBSCRIBED, pocketflow_unsubscribed 'sms', provenance
//   `sms_stop`). Marketing stays the only writer of SMS consent.
// detailType in EventBridge: `messaging.receipt_sms.stopped.v1` (Commitment 6).
//
// Status: active

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';

/** Lowercase hex sha256 of an E.164 number. */
export const receiptSmsPhoneHashSchema = z.string().regex(/^[0-9a-f]{64}$/, {
  message: 'toPhoneHash must be a lowercase hex sha256',
});

/** The most contacts one event may name; a DynamoDB BatchGetItem holds 100 keys. */
export const RECEIPT_SMS_STOPPED_MAX_CONTACTS = 100;

export const messagingReceiptSmsStoppedV1Schema = eventEnvelopeSchema.extend({
  toPhoneHash: receiptSmsPhoneHashSchema,
  // The contacts this organization texted at that number (from the texted rows'
  // contactId). May be empty when the texted rows carried no contact; the
  // consumer then has nothing to suppress.
  contactIds: z
    .array(z.string().min(1).max(256))
    .max(RECEIPT_SMS_STOPPED_MAX_CONTACTS),
  // When the donor sent the STOP (the provider's time for the inbound message).
  // The consumer's staleness guard compares it with the contact's last SMS
  // consent write, so a re-grant made after the STOP is never overwritten.
  receivedAt: z.string().datetime({ offset: true }),
});

export type MessagingReceiptSmsStoppedV1 = z.infer<typeof messagingReceiptSmsStoppedV1Schema>;

export const MESSAGING_RECEIPT_SMS_STOPPED_V1_DETAIL_TYPE =
  'messaging.receipt_sms.stopped.v1' as const;

/** EventBridge `source` the messaging publisher stamps on every event. */
export const MESSAGING_EVENT_SOURCE = 'charitystack.messaging' as const;
